import React, { useState, useEffect } from 'react';
import { adminApi } from '../services/api';
import Pagination from './Pagination';

const AdminEventManagement = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusMessage, setStatusMessage] = useState('');

  // Filters & Search
  const [activeFilter, setActiveFilter] = useState('all'); // 'all', 'pending', 'approved', 'rejected'
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [modeFilter, setModeFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Selected event for Detail View Modal
  const [selectedEvent, setSelectedEvent] = useState(null);

  // Rejection Note Modal
  const [reviewNoteModal, setReviewNoteModal] = useState(null); // { eventId, decision: 'approved' | 'rejected' }
  const [reviewNoteInput, setReviewNoteInput] = useState('');

  // Create / Edit Event Modal State
  const [eventModal, setEventModal] = useState({
    isOpen: false,
    mode: 'create', // 'create' or 'edit'
    eventId: null,
  });

  const [publishPlans, setPublishPlans] = useState([]);

  const [eventFormData, setEventFormData] = useState({
    title: '',
    description: '',
    eventType: 'seminar',
    mode: 'online',
    publishWeeks: 1,
    publishPlanId: '',
    scheduledAt: '',
    endsAt: '',
    location: '',
    eventFee: 'Free',
    registrationLink: '',
    meetingLink: '',
    videoUrl: '',
    videoFileName: '',
    videoFileSize: 0,
    status: 'approved', // Default direct approve for admin
  });

  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [videoUploadError, setVideoUploadError] = useState('');

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  // Video File Upload Handler
  const handleVideoFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setUploadingVideo(true);
      setVideoUploadError('');

      const formData = new FormData();
      formData.append('video', file);

      const res = await adminApi.uploadEventVideo(formData);
      if (res?.url) {
        setEventFormData((prev) => ({
          ...prev,
          videoUrl: res.url,
          videoFileName: res.fileName || file.name,
          videoFileSize: res.fileSize || file.size,
        }));
      } else {
        throw new Error(res?.message || 'Failed to upload video');
      }
    } catch (err) {
      console.error('Video upload error:', err);
      setVideoUploadError(err.message || 'Failed to upload video file');
    } finally {
      setUploadingVideo(false);
      e.target.value = ''; // Reset file input
    }
  };

  const handleRemoveVideo = () => {
    setEventFormData((prev) => ({
      ...prev,
      videoUrl: '',
      videoFileName: '',
      videoFileSize: 0,
    }));
  };

  // Fetch events from API
  const fetchEvents = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await adminApi.getEvents({ limit: 500 });
      const eventsList = response.events || response.data || (Array.isArray(response) ? response : []);
      setEvents(eventsList);
    } catch (err) {
      console.error('Failed to load admin events:', err);
      setError(err.message || 'Failed to load events list from server');
    } finally {
      setLoading(false);
    }
  };

  const fetchPublishPlans = async () => {
    try {
      const res = await adminApi.getEventPublishPlans({ all: 'true' });
      const list = res.plans || res.data || (Array.isArray(res) ? res : []);
      setPublishPlans(list);
    } catch (err) {
      console.error('Failed to load publish plans:', err);
    }
  };

  useEffect(() => {
    fetchEvents();
    fetchPublishPlans();
  }, []);

  // Helper to format ISO date string for datetime-local input (YYYY-MM-DDTHH:mm)
  const formatForDatetimeLocal = (dateStr) => {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return '';
      const pad = (n) => String(n).padStart(2, '0');
      const year = d.getFullYear();
      const month = pad(d.getMonth() + 1);
      const day = pad(d.getDate());
      const hours = pad(d.getHours());
      const minutes = pad(d.getMinutes());
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    } catch {
      return '';
    }
  };

  // Helper to calculate End Date given a start date and weeks
  const calculateEndDate = (startDateStr, weeks) => {
    if (!startDateStr) return '';
    try {
      const start = new Date(startDateStr);
      if (isNaN(start.getTime())) return '';
      const weeksNum = Math.max(1, parseInt(weeks, 10) || 1);
      const end = new Date(start.getTime() + weeksNum * 7 * 24 * 60 * 60 * 1000);
      return formatForDatetimeLocal(end.toISOString());
    } catch {
      return '';
    }
  };

  // Open Create Modal
  const handleOpenCreateModal = () => {
    const now = new Date();
    now.setHours(now.getHours() + 1, 0, 0, 0);
    const defaultStart = now.toISOString();
    const defaultWeeks = 1;
    const defaultEnd = new Date(now.getTime() + defaultWeeks * 7 * 24 * 60 * 60 * 1000).toISOString();

    const matchingPlan = publishPlans.find((p) => p.weeks === 1) || publishPlans[0];

    setEventFormData({
      title: '',
      description: '',
      eventType: 'seminar',
      mode: 'online',
      publishWeeks: defaultWeeks,
      publishPlanId: matchingPlan ? (matchingPlan._id || matchingPlan.id) : '',
      publishPrice: matchingPlan ? matchingPlan.price : 499,
      scheduledAt: formatForDatetimeLocal(defaultStart),
      endsAt: formatForDatetimeLocal(defaultEnd),
      location: '',
      eventFee: 'Free',
      registrationLink: '',
      meetingLink: '',
      videoUrl: '',
      videoFileName: '',
      videoFileSize: 0,
      status: 'approved', // Direct approve by default
    });
    setVideoUploadError('');
    setFormError('');
    setEventModal({ isOpen: true, mode: 'create', eventId: null });
  };

  // Open Edit Modal
  const handleOpenEditModal = (evt) => {
    const weeks = evt.publishWeeks || 1;
    const calculatedEnd = evt.endsAt
      ? formatForDatetimeLocal(evt.endsAt)
      : calculateEndDate(evt.scheduledAt, weeks);

    setEventFormData({
      title: evt.title || '',
      description: evt.description || '',
      eventType: evt.eventType || 'seminar',
      mode: evt.mode || 'online',
      publishWeeks: weeks,
      publishPlanId: evt.publishPlanId?._id || evt.publishPlanId || '',
      publishPrice: evt.publishPrice || 0,
      scheduledAt: formatForDatetimeLocal(evt.scheduledAt),
      endsAt: calculatedEnd,
      location: evt.location || '',
      eventFee: evt.eventFee || 'Free',
      registrationLink: evt.registrationLink || '',
      meetingLink: evt.meetingLink || '',
      videoUrl: evt.videoUrl || '',
      videoFileName: evt.videoFileName || '',
      videoFileSize: evt.videoFileSize || 0,
      status: evt.status || 'approved',
    });
    setVideoUploadError('');
    setFormError('');
    setEventModal({ isOpen: true, mode: 'edit', eventId: evt._id });
    if (selectedEvent && selectedEvent._id === evt._id) {
      setSelectedEvent(null);
    }
  };

  // Handle Week Change & Recalculate End Date
  const handleWeekSelection = (selectedWeeks, plan = null) => {
    const weeksNum = Math.max(1, parseInt(selectedWeeks, 10) || 1);
    const updatedEnd = calculateEndDate(eventFormData.scheduledAt, weeksNum);
    setEventFormData((prev) => ({
      ...prev,
      publishWeeks: weeksNum,
      publishPlanId: plan ? (plan._id || plan.id) : prev.publishPlanId,
      publishPrice: plan ? plan.price : prev.publishPrice,
      endsAt: updatedEnd || prev.endsAt,
    }));
  };

  // Handle Start Date Change & Recalculate End Date
  const handleStartDateChange = (newStart) => {
    const updatedEnd = calculateEndDate(newStart, eventFormData.publishWeeks);
    setEventFormData((prev) => ({
      ...prev,
      scheduledAt: newStart,
      endsAt: updatedEnd || prev.endsAt,
    }));
  };

  // Submit Create or Edit Form
  const handleEventFormSubmit = async (e) => {
    e.preventDefault();
    if (!eventFormData.title.trim()) {
      setFormError('Event title is required');
      return;
    }
    if (!eventFormData.scheduledAt) {
      setFormError('Scheduled start date & time is required');
      return;
    }

    try {
      setIsSubmitting(true);
      setFormError('');

      const computedEnd =
        eventFormData.endsAt ||
        calculateEndDate(eventFormData.scheduledAt, eventFormData.publishWeeks) ||
        new Date(new Date(eventFormData.scheduledAt).getTime() + (parseInt(eventFormData.publishWeeks, 10) || 1) * 7 * 24 * 60 * 60 * 1000).toISOString();

      const payload = {
        title: eventFormData.title.trim(),
        description: eventFormData.description.trim(),
        eventType: eventFormData.eventType,
        mode: eventFormData.mode,
        publishWeeks: parseInt(eventFormData.publishWeeks, 10) || 1,
        publishPlanId: eventFormData.publishPlanId || null,
        publishPrice: parseFloat(eventFormData.publishPrice) || 0,
        scheduledAt: new Date(eventFormData.scheduledAt).toISOString(),
        endsAt: new Date(computedEnd).toISOString(),
        location: eventFormData.mode === 'offline' ? eventFormData.location.trim() : '',
        eventFee: eventFormData.eventFee ? eventFormData.eventFee.trim() : 'Free',
        registrationLink: eventFormData.registrationLink ? eventFormData.registrationLink.trim() : '',
        meetingLink: eventFormData.mode === 'online' ? eventFormData.meetingLink.trim() : '',
        videoUrl: eventFormData.videoUrl ? eventFormData.videoUrl.trim() : '',
        videoFileName: eventFormData.videoFileName ? eventFormData.videoFileName.trim() : '',
        videoFileSize: parseInt(eventFormData.videoFileSize, 10) || 0,
        status: eventFormData.status || 'approved',
      };

      if (eventModal.mode === 'create') {
        const res = await adminApi.createEvent(payload);
        const newEvt = res.event || res;
        setEvents((prev) => [newEvt, ...prev]);
        setStatusMessage(`Event created and directly ${payload.status} successfully!`);
        setEventModal({ isOpen: false, mode: 'create', eventId: null });
      } else {
        const res = await adminApi.updateEvent(eventModal.eventId, payload);
        const updatedEvt = res.event || res;
        setEvents((prev) =>
          prev.map((ev) => (ev._id === eventModal.eventId ? { ...ev, ...updatedEvt } : ev))
        );
        setStatusMessage('Event updated successfully!');
        setEventModal({ isOpen: false, mode: 'edit', eventId: null });
      }

      setTimeout(() => setStatusMessage(''), 4000);
      fetchEvents();
    } catch (err) {
      console.error('Error saving event:', err);
      setFormError(err.message || 'Failed to save event');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle Quick Approve / Reject decision
  const handleReviewDecision = async (eventId, decision, note = '') => {
    try {
      setIsSubmitting(true);
      setError('');

      // Optimistic update
      setEvents((prev) =>
        prev.map((ev) => (ev._id === eventId ? { ...ev, status: decision, reviewNote: note } : ev))
      );

      const response = await adminApi.reviewEvent(eventId, {
        decision,
        reviewNote: note,
      });

      const updatedEvent = response.event || response;
      setEvents((prev) =>
        prev.map((ev) => (ev._id === eventId ? { ...ev, ...updatedEvent } : ev))
      );

      setStatusMessage(`Event successfully ${decision}!`);
      setTimeout(() => setStatusMessage(''), 4000);

      setReviewNoteModal(null);
      if (selectedEvent && selectedEvent._id === eventId) {
        setSelectedEvent({ ...selectedEvent, ...updatedEvent });
      }
    } catch (err) {
      console.error('Error reviewing event:', err);
      setError(err.message || `Failed to ${decision} event.`);
      fetchEvents(); // Revert on failure
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle Delete
  const handleDeleteEvent = async (eventId) => {
    const evt = events.find((e) => e._id === eventId);
    const evtTitle = evt ? evt.title : 'this event';

    if (!window.confirm(`Are you sure you want to delete "${evtTitle}"?`)) return;

    try {
      setIsSubmitting(true);
      // Optimistic delete
      setEvents((prev) => prev.filter((ev) => ev._id !== eventId));
      if (selectedEvent && selectedEvent._id === eventId) {
        setSelectedEvent(null);
      }

      await adminApi.deleteEvent(eventId);
      setStatusMessage('Event deleted successfully!');
      setTimeout(() => setStatusMessage(''), 4000);
    } catch (err) {
      console.error('Error deleting event:', err);
      setError(err.message || 'Failed to delete event');
      fetchEvents(); // Revert on failure
    } finally {
      setIsSubmitting(false);
    }
  };

  // Dynamic Real-time Counters
  const totalCount = events.length;
  const pendingCount = events.filter((e) => e.status === 'pending').length;
  const approvedCount = events.filter((e) => e.status === 'approved').length;
  const rejectedCount = events.filter((e) => e.status === 'rejected').length;

  // Filtered Events List
  const filteredEvents = events.filter((ev) => {
    if (activeFilter !== 'all' && ev.status !== activeFilter) return false;
    if (categoryFilter !== 'all' && ev.eventType !== categoryFilter) return false;
    if (modeFilter !== 'all' && ev.mode !== modeFilter) return false;

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const title = ev.title?.toLowerCase() || '';
      const host = ev.createdBy?.name?.toLowerCase() || '';
      const email = ev.createdBy?.email?.toLowerCase() || '';
      const desc = ev.description?.toLowerCase() || '';
      const loc = ev.location?.toLowerCase() || '';

      return (
        title.includes(q) ||
        host.includes(q) ||
        email.includes(q) ||
        desc.includes(q) ||
        loc.includes(q)
      );
    }

    return true;
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [activeFilter, categoryFilter, modeFilter, searchQuery]);

  const totalPages = Math.ceil(filteredEvents.length / itemsPerPage);
  const paginatedEvents = filteredEvents.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <div style={{ padding: '20px' }}>
      {/* Top Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '28px',
          flexWrap: 'wrap',
          gap: '15px',
        }}
      >
        <div>
          <h2
            style={{
              margin: '0 0 6px 0',
              fontSize: '1.8rem',
              fontWeight: 800,
              color: '#1e293b',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
            }}
          >
            🎉 Event & Seminar Management
          </h2>
          <p style={{ margin: 0, color: '#64748b', fontSize: '0.95rem' }}>
            Create, edit, review, approve (direct approve), or remove seminars and clinical workshops.
          </p>
        </div>

        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <button
            onClick={fetchEvents}
            style={{
              padding: '10px 18px',
              background: '#ffffff',
              color: '#475569',
              border: '1.5px solid #cbd5e1',
              borderRadius: '10px',
              fontWeight: '600',
              cursor: 'pointer',
              fontSize: '0.9rem',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            🔄 Refresh
          </button>

          <button
            onClick={handleOpenCreateModal}
            style={{
              padding: '10px 22px',
              background: 'linear-gradient(135deg, #7c3aed 0%, #6366f1 100%)',
              color: '#ffffff',
              border: 'none',
              borderRadius: '10px',
              fontWeight: 700,
              cursor: 'pointer',
              fontSize: '0.95rem',
              boxShadow: '0 4px 14px rgba(124, 58, 237, 0.25)',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <span>➕</span> Create New Event
          </button>
        </div>
      </div>

      {/* Alert Messages */}
      {statusMessage && (
        <div
          style={{
            background: '#ecfdf5',
            border: '1.5px solid #a7f3d0',
            color: '#065f46',
            padding: '12px 18px',
            borderRadius: '10px',
            marginBottom: '20px',
            fontWeight: 600,
            fontSize: '0.92rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <span>✓ {statusMessage}</span>
          <button
            onClick={() => setStatusMessage('')}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', fontWeight: 'bold' }}
          >
            ✕
          </button>
        </div>
      )}

      {error && (
        <div
          style={{
            background: '#fef2f2',
            border: '1.5px solid #fecaca',
            color: '#991b1b',
            padding: '12px 18px',
            borderRadius: '10px',
            marginBottom: '20px',
            fontWeight: 600,
            fontSize: '0.92rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <span>⚠️ {error}</span>
          <button
            onClick={() => setError('')}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', fontWeight: 'bold' }}
          >
            ✕
          </button>
        </div>
      )}

      {/* Metric Cards (Real-time Dynamic Counters) */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
          gap: '20px',
          marginBottom: '28px',
        }}
      >
        <div style={{ background: '#ffffff', padding: '20px', borderRadius: '14px', border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
          <div style={{ color: '#64748b', fontSize: '0.82rem', fontWeight: 700, textTransform: 'uppercase', marginBottom: '6px' }}>
            TOTAL EVENTS
          </div>
          <div style={{ fontSize: '1.8rem', fontWeight: 800, color: '#1e293b' }}>{totalCount}</div>
        </div>

        <div style={{ background: '#ffffff', padding: '20px', borderRadius: '14px', border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
          <div style={{ color: '#059669', fontSize: '0.82rem', fontWeight: 700, textTransform: 'uppercase', marginBottom: '6px' }}>
            ✓ APPROVED (LIVE)
          </div>
          <div style={{ fontSize: '1.8rem', fontWeight: 800, color: '#059669' }}>{approvedCount}</div>
        </div>

        <div style={{ background: '#ffffff', padding: '20px', borderRadius: '14px', border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
          <div style={{ color: '#d97706', fontSize: '0.82rem', fontWeight: 700, textTransform: 'uppercase', marginBottom: '6px' }}>
            ⏳ PENDING REVIEW
          </div>
          <div style={{ fontSize: '1.8rem', fontWeight: 800, color: '#d97706' }}>{pendingCount}</div>
        </div>

        <div style={{ background: '#ffffff', padding: '20px', borderRadius: '14px', border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
          <div style={{ color: '#dc2626', fontSize: '0.82rem', fontWeight: 700, textTransform: 'uppercase', marginBottom: '6px' }}>
            ✕ REJECTED
          </div>
          <div style={{ fontSize: '1.8rem', fontWeight: 800, color: '#dc2626' }}>{rejectedCount}</div>
        </div>
      </div>

      {/* Filters & Search Toolbar */}
      <div
        style={{
          background: '#ffffff',
          padding: '18px 24px',
          borderRadius: '16px',
          border: '1px solid #e2e8f0',
          marginBottom: '24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '16px',
        }}
      >
        {/* Status Filter Tabs */}
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {[
            { key: 'all', label: `All (${totalCount})` },
            { key: 'pending', label: `⏳ Pending (${pendingCount})` },
            { key: 'approved', label: `✓ Approved (${approvedCount})` },
            { key: 'rejected', label: `✕ Rejected (${rejectedCount})` },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveFilter(tab.key)}
              style={{
                padding: '7px 14px',
                borderRadius: '20px',
                border: activeFilter === tab.key ? '1.5px solid #7c3aed' : '1px solid #e2e8f0',
                fontWeight: '700',
                fontSize: '0.82rem',
                cursor: 'pointer',
                background: activeFilter === tab.key ? '#7c3aed' : '#f8fafc',
                color: activeFilter === tab.key ? '#ffffff' : '#64748b',
                transition: 'all 0.2s',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Dropdowns & Search Box */}
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            style={{
              padding: '9px 12px',
              border: '1.5px solid #cbd5e1',
              borderRadius: '8px',
              fontSize: '0.88rem',
              background: '#fff',
              outline: 'none',
              fontWeight: 600,
            }}
          >
            <option value="all">All Event Types</option>
            <option value="seminar">🎤 Seminar</option>
            <option value="workshop">🛠️ Workshop</option>
            <option value="event">🎉 Special Event</option>
          </select>

          <select
            value={modeFilter}
            onChange={(e) => setModeFilter(e.target.value)}
            style={{
              padding: '9px 12px',
              border: '1.5px solid #cbd5e1',
              borderRadius: '8px',
              fontSize: '0.88rem',
              background: '#fff',
              outline: 'none',
              fontWeight: 600,
            }}
          >
            <option value="all">All Modes</option>
            <option value="online">💻 Online</option>
            <option value="offline">🏥 Offline</option>
          </select>

          <input
            type="text"
            placeholder="🔍 Search title, host, location..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              padding: '9px 14px',
              border: '1.5px solid #cbd5e1',
              borderRadius: '8px',
              fontSize: '0.88rem',
              width: '230px',
              outline: 'none',
            }}
          />
        </div>
      </div>

      {/* Events Table */}
      <div
        style={{
          background: '#ffffff',
          borderRadius: '16px',
          border: '1px solid #e2e8f0',
          overflow: 'hidden',
          boxShadow: '0 4px 16px rgba(0,0,0,0.02)',
        }}
      >
        {loading ? (
          <div style={{ padding: '60px', textAlign: 'center' }}>
            <div className="spinner-border text-primary" role="status" style={{ width: '2.5rem', height: '2.5rem', color: '#7c3aed' }}></div>
            <p style={{ marginTop: '14px', color: '#64748b', fontWeight: 600 }}>Loading events list...</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '1.5px solid #e2e8f0', color: '#475569', fontSize: '0.82rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  <th style={{ padding: '16px 20px' }}>Event Title</th>
                  <th style={{ padding: '16px 20px' }}>Host / Creator</th>
                  <th style={{ padding: '16px 20px' }}>Category & Mode</th>
                  <th style={{ padding: '16px 20px' }}>Date & Time</th>
                  <th style={{ padding: '16px 20px', textAlign: 'center' }}>Status</th>
                  <th style={{ padding: '16px 20px', textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedEvents.map((evt) => {
                  const startDate = evt.scheduledAt
                    ? new Date(evt.scheduledAt).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })
                    : 'TBD';
                  const startTime = evt.scheduledAt
                    ? new Date(evt.scheduledAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                    : '';
                  const hostName = evt.createdBy?.name || 'Administrator';

                  const isApproved = evt.status === 'approved';
                  const isRejected = evt.status === 'rejected';

                  return (
                    <tr key={evt._id} style={{ borderBottom: '1px solid #f1f5f9', transition: 'background 0.15s' }}>
                      {/* Event Title */}
                      <td style={{ padding: '16px 20px', color: '#1e293b' }}>
                        <div style={{ fontSize: '0.95rem', fontWeight: 700 }}>{evt.title}</div>
                        {evt.description && (
                          <div style={{ color: '#64748b', fontSize: '0.82rem', marginTop: '3px', maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {evt.description}
                          </div>
                        )}
                      </td>

                      {/* Author / Host */}
                      <td style={{ padding: '16px 20px', color: '#475569', fontSize: '0.9rem' }}>
                        <div style={{ fontWeight: 600, color: '#1e293b' }}>👤 {hostName}</div>
                        <div style={{ fontSize: '0.78rem', color: '#94a3b8' }}>{evt.createdBy?.email || ''}</div>
                      </td>

                      {/* Category & Mode Badge */}
                      <td style={{ padding: '16px 20px' }}>
                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                          <span
                            style={{
                              padding: '4px 10px',
                              background: '#f5f3ff',
                              color: '#7c3aed',
                              border: '1px solid #ddd6fe',
                              borderRadius: '20px',
                              fontSize: '0.78rem',
                              fontWeight: '700',
                              textTransform: 'capitalize',
                            }}
                          >
                            {evt.eventType || 'seminar'}
                          </span>
                          <span
                            style={{
                              padding: '4px 10px',
                              background: evt.mode === 'online' ? '#eff6ff' : '#ecfdf5',
                              color: evt.mode === 'online' ? '#1d4ed8' : '#047857',
                              border: `1px solid ${evt.mode === 'online' ? '#bfdbfe' : '#a7f3d0'}`,
                              borderRadius: '20px',
                              fontSize: '0.78rem',
                              fontWeight: '700',
                            }}
                          >
                            {evt.mode === 'online' ? '💻 Online' : '🏥 Offline'}
                          </span>
                        </div>
                      </td>

                      {/* Date & Publish Duration */}
                      <td style={{ padding: '16px 20px', color: '#475569', fontSize: '0.88rem' }}>
                        <div style={{ fontWeight: 700, color: '#1e293b' }}>📅 {startDate} {startTime && `(${startTime})`}</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '4px', flexWrap: 'wrap' }}>
                          <span style={{ background: '#f5f3ff', color: '#7c3aed', padding: '2px 7px', borderRadius: '6px', fontSize: '0.74rem', fontWeight: 700, border: '1px solid #ddd6fe' }}>
                            🗓️ {evt.publishWeeks || 1} {evt.publishWeeks === 1 ? 'Week' : 'Weeks'}
                          </span>
                          {evt.endsAt && (
                            <span style={{ fontSize: '0.75rem', color: '#059669', fontWeight: 600 }}>
                              Until: {new Date(evt.endsAt).toLocaleDateString([], { month: 'short', day: 'numeric' })}
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Status Badge */}
                      <td style={{ padding: '16px 20px', textAlign: 'center' }}>
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            padding: '4px 12px',
                            background: isApproved ? '#ecfdf5' : isRejected ? '#fef2f2' : '#fffbeb',
                            color: isApproved ? '#065f46' : isRejected ? '#991b1b' : '#92400e',
                            border: `1px solid ${isApproved ? '#a7f3d0' : isRejected ? '#fecaca' : '#fde68a'}`,
                            borderRadius: '20px',
                            fontSize: '0.78rem',
                            fontWeight: '700',
                            textTransform: 'capitalize',
                          }}
                        >
                          <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: isApproved ? '#10b981' : isRejected ? '#ef4444' : '#f59e0b' }}></span>
                          {isApproved ? '✓ Approved' : isRejected ? '✕ Rejected' : '⏳ Pending'}
                        </span>
                      </td>

                      {/* Actions */}
                      <td style={{ padding: '16px 20px', textAlign: 'right' }}>
                        <div style={{ display: 'inline-flex', gap: '6px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                          <button
                            type="button"
                            onClick={() => setSelectedEvent(evt)}
                            style={{
                              padding: '5px 10px',
                              background: '#f8fafc',
                              color: '#334155',
                              border: '1.5px solid #cbd5e1',
                              borderRadius: '8px',
                              fontSize: '0.8rem',
                              fontWeight: '600',
                              cursor: 'pointer',
                            }}
                          >
                            👁️ View
                          </button>

                          <button
                            type="button"
                            onClick={() => handleOpenEditModal(evt)}
                            style={{
                              padding: '5px 10px',
                              background: '#f5f3ff',
                              color: '#7c3aed',
                              border: '1.5px solid #ddd6fe',
                              borderRadius: '8px',
                              fontSize: '0.8rem',
                              fontWeight: '600',
                              cursor: 'pointer',
                            }}
                          >
                            ✏️ Edit
                          </button>

                          {!isApproved && (
                            <button
                              type="button"
                              onClick={() => handleReviewDecision(evt._id, 'approved')}
                              disabled={isSubmitting}
                              style={{
                                padding: '5px 10px',
                                background: '#ecfdf5',
                                color: '#065f46',
                                border: '1.5px solid #a7f3d0',
                                borderRadius: '8px',
                                fontSize: '0.8rem',
                                fontWeight: '700',
                                cursor: 'pointer',
                              }}
                            >
                              ✓ Approve
                            </button>
                          )}

                          {!isRejected && (
                            <button
                              type="button"
                              onClick={() => {
                                setReviewNoteModal({ eventId: evt._id, decision: 'rejected' });
                                setReviewNoteInput('');
                              }}
                              disabled={isSubmitting}
                              style={{
                                padding: '5px 10px',
                                background: '#fffbeb',
                                color: '#92400e',
                                border: '1.5px solid #fde68a',
                                borderRadius: '8px',
                                fontSize: '0.8rem',
                                fontWeight: '600',
                                cursor: 'pointer',
                              }}
                            >
                              ✕ Reject
                            </button>
                          )}

                          <button
                            type="button"
                            onClick={() => handleDeleteEvent(evt._id)}
                            disabled={isSubmitting}
                            style={{
                              padding: '5px 10px',
                              background: '#fff5f5',
                              color: '#dc2626',
                              border: '1.5px solid #fecaca',
                              borderRadius: '8px',
                              fontSize: '0.8rem',
                              fontWeight: '600',
                              cursor: 'pointer',
                            }}
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {!loading && filteredEvents.length === 0 && (
          <div style={{ padding: '60px 20px', textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '12px' }}>🎉</div>
            <h4 style={{ fontWeight: 700, color: '#1e293b', marginBottom: '6px' }}>No events found</h4>
            <p style={{ color: '#64748b', margin: '0 auto 16px auto', maxWidth: '400px', fontSize: '0.9rem' }}>
              No events matched the current search or filters. Create an event below.
            </p>
            <button
              onClick={handleOpenCreateModal}
              style={{
                padding: '10px 20px',
                background: '#7c3aed',
                color: '#ffffff',
                border: 'none',
                borderRadius: '8px',
                fontWeight: 600,
                fontSize: '0.9rem',
                cursor: 'pointer',
              }}
            >
              ➕ Create First Event
            </button>
          </div>
        )}

        {!loading && filteredEvents.length > 0 && (
          <div style={{ padding: '12px 20px', borderTop: '1px solid #f1f5f9' }}>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={filteredEvents.length}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={(newSize) => {
                setItemsPerPage(newSize);
                setCurrentPage(1);
              }}
            />
          </div>
        )}
      </div>

      {/* ========================================================================= */}
      {/* CREATE / EDIT EVENT MODAL (WITH DIRECT APPROVE OPTION) */}
      {/* ========================================================================= */}
      {eventModal.isOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(15, 23, 42, 0.65)',
            backdropFilter: 'blur(5px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '20px',
          }}
          onClick={() => !isSubmitting && setEventModal({ isOpen: false, mode: 'create', eventId: null })}
        >
          <div
            style={{
              background: '#ffffff',
              borderRadius: '20px',
              padding: '28px',
              maxWidth: '650px',
              width: '100%',
              maxHeight: '92vh',
              overflowY: 'auto',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.3)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', borderBottom: '1px solid #f1f5f9', paddingBottom: '16px' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 800, color: '#1e293b' }}>
                  {eventModal.mode === 'create' ? '➕ Create Event (Admin Direct Approve)' : '✏️ Edit Event Details'}
                </h3>
                <p style={{ margin: '4px 0 0 0', fontSize: '0.85rem', color: '#64748b' }}>
                  {eventModal.mode === 'create'
                    ? 'Publish a new workshop or seminar with immediate approval'
                    : 'Update event parameters, timings, and publishing status'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setEventModal({ isOpen: false, mode: 'create', eventId: null })}
                style={{ background: '#f1f5f9', border: 'none', borderRadius: '50%', width: '34px', height: '34px', cursor: 'pointer', fontSize: '1.1rem', color: '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                ✕
              </button>
            </div>

            {/* Error in modal */}
            {formError && (
              <div style={{ padding: '12px 16px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '10px', color: '#dc2626', fontSize: '0.88rem', marginBottom: '18px', fontWeight: 600 }}>
                ⚠️ {formError}
              </div>
            )}

            <form onSubmit={handleEventFormSubmit}>
              {/* Event Title */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '0.88rem', fontWeight: 700, color: '#1e293b', marginBottom: '6px' }}>
                  Event Title <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Cognitive Behavioral Therapy Masterclass, Stress Management Workshop"
                  value={eventFormData.title}
                  onChange={(e) => setEventFormData({ ...eventFormData, title: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '11px 14px',
                    borderRadius: '10px',
                    border: '1.5px solid #cbd5e1',
                    fontSize: '0.92rem',
                    outline: 'none',
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              {/* Event Type & Mode */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '16px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.88rem', fontWeight: 700, color: '#1e293b', marginBottom: '6px' }}>
                    Event Category
                  </label>
                  <select
                    value={eventFormData.eventType}
                    onChange={(e) => setEventFormData({ ...eventFormData, eventType: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '11px 14px',
                      borderRadius: '10px',
                      border: '1.5px solid #cbd5e1',
                      fontSize: '0.92rem',
                      outline: 'none',
                      background: '#fff',
                      boxSizing: 'border-box',
                    }}
                  >
                    <option value="seminar">🎤 Seminar</option>
                    <option value="workshop">🛠️ Workshop</option>
                    <option value="event">🎉 Special Event</option>
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.88rem', fontWeight: 700, color: '#1e293b', marginBottom: '6px' }}>
                    Event Mode
                  </label>
                  <select
                    value={eventFormData.mode}
                    onChange={(e) => setEventFormData({ ...eventFormData, mode: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '11px 14px',
                      borderRadius: '10px',
                      border: '1.5px solid #cbd5e1',
                      fontSize: '0.92rem',
                      outline: 'none',
                      background: '#fff',
                      boxSizing: 'border-box',
                    }}
                  >
                    <option value="online">💻 Online Video Session</option>
                    <option value="offline">🏥 Offline / On-Premise Venue</option>
                  </select>
                </div>
              </div>

              {/* Publish Duration (Weeks) Selection */}
              <div style={{ marginBottom: '18px', padding: '16px', background: '#f8fafc', borderRadius: '12px', border: '1.5px solid #e2e8f0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <label style={{ fontSize: '0.88rem', fontWeight: 700, color: '#1e293b', margin: 0 }}>
                    🗓️ Publish Duration (Weeks Option)
                  </label>
                  <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#7c3aed', background: '#f5f3ff', padding: '2px 8px', borderRadius: '10px' }}>
                    {eventFormData.publishWeeks} Week{eventFormData.publishWeeks > 1 ? 's' : ''} ({eventFormData.publishWeeks * 7} Days)
                  </span>
                </div>

                {/* Quick Week Options / Pricing Tiers Pills */}
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
                  {(publishPlans.length > 0
                    ? publishPlans
                    : [
                        { weeks: 1, name: '1 Week', price: 499 },
                        { weeks: 2, name: '2 Weeks', price: 899 },
                        { weeks: 3, name: '3 Weeks', price: 1299 },
                        { weeks: 4, name: '4 Weeks', price: 1699 },
                      ]
                  ).map((plan, idx) => {
                    const isSelected = parseInt(eventFormData.publishWeeks, 10) === parseInt(plan.weeks, 10);
                    return (
                      <button
                        key={plan._id || plan.id || idx}
                        type="button"
                        onClick={() => handleWeekSelection(plan.weeks, plan)}
                        style={{
                          padding: '7px 14px',
                          borderRadius: '8px',
                          border: isSelected ? '1.5px solid #7c3aed' : '1.5px solid #cbd5e1',
                          background: isSelected ? '#f5f3ff' : '#ffffff',
                          color: isSelected ? '#7c3aed' : '#475569',
                          fontWeight: isSelected ? 700 : 500,
                          fontSize: '0.82rem',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          transition: 'all 0.15s ease',
                        }}
                      >
                        <span>🗓️ {plan.weeks} {plan.weeks === 1 ? 'Week' : 'Weeks'}</span>
                        {plan.price !== undefined && (
                          <span style={{ color: isSelected ? '#059669' : '#64748b', fontWeight: 700 }}>
                            ₹{plan.price}
                          </span>
                        )}
                        {isSelected && <span style={{ color: '#7c3aed' }}>✓</span>}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Scheduled Start Time */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '0.88rem', fontWeight: 700, color: '#1e293b', marginBottom: '6px' }}>
                  Start Date & Time <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                  type="datetime-local"
                  required
                  value={eventFormData.scheduledAt}
                  onChange={(e) => handleStartDateChange(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    borderRadius: '10px',
                    border: '1.5px solid #cbd5e1',
                    fontSize: '0.88rem',
                    outline: 'none',
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              {/* Real-time Calculation Badge */}
              {eventFormData.scheduledAt && eventFormData.endsAt && (
                <div style={{ padding: '10px 14px', background: '#ecfdf5', borderRadius: '10px', border: '1px solid #a7f3d0', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.82rem', color: '#065f46', fontWeight: 600 }}>
                  <span>✨</span>
                  <span>
                    Event will be visible on front side from{' '}
                    <strong>{new Date(eventFormData.scheduledAt).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</strong>{' '}
                    until{' '}
                    <strong>{new Date(eventFormData.endsAt).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</strong>{' '}
                    ({eventFormData.publishWeeks} Week{eventFormData.publishWeeks > 1 ? 's' : ''} / {eventFormData.publishWeeks * 7} Days total).
                  </span>
                </div>
              )}

              {/* Venue Address (for Offline events) */}
              {eventFormData.mode === 'offline' && (
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontSize: '0.88rem', fontWeight: 700, color: '#1e293b', marginBottom: '6px' }}>
                    Venue / Clinic Address
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. Health Hub, 4th Floor, City Center Clinic"
                    value={eventFormData.location}
                    onChange={(e) => setEventFormData({ ...eventFormData, location: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '11px 14px',
                      borderRadius: '10px',
                      border: '1.5px solid #cbd5e1',
                      fontSize: '0.92rem',
                      outline: 'none',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>
              )}

              {/* Event Fee & Client Registration Link */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '16px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.88rem', fontWeight: 700, color: '#1e293b', marginBottom: '6px' }}>
                    🎟️ Event Fee (for Clients/Attendees)
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. Free, ₹499, ₹1,200 / attendee"
                    value={eventFormData.eventFee}
                    onChange={(e) => setEventFormData({ ...eventFormData, eventFee: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '11px 14px',
                      borderRadius: '10px',
                      border: '1.5px solid #cbd5e1',
                      fontSize: '0.92rem',
                      outline: 'none',
                      boxSizing: 'border-box',
                    }}
                  />
                  <span style={{ fontSize: '0.76rem', color: '#64748b', marginTop: '3px', display: 'block' }}>Informational for clients (e.g. Free or fee)</span>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.88rem', fontWeight: 700, color: '#1e293b', marginBottom: '6px' }}>
                    🔗 Client Registration Link (URL)
                  </label>
                  <input
                    type="url"
                    placeholder="https://forms.gle/... or https://zoom.us/..."
                    value={eventFormData.registrationLink}
                    onChange={(e) => setEventFormData({ ...eventFormData, registrationLink: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '11px 14px',
                      borderRadius: '10px',
                      border: '1.5px solid #cbd5e1',
                      fontSize: '0.92rem',
                      outline: 'none',
                      boxSizing: 'border-box',
                    }}
                  />
                  <span style={{ fontSize: '0.76rem', color: '#64748b', marginTop: '3px', display: 'block' }}>External form or meeting registration link</span>
                </div>
              </div>

              {/* Upload File / Video Section */}
              <div style={{ marginBottom: '18px', background: '#f8fafc', padding: '16px', borderRadius: '12px', border: '1.5px solid #e2e8f0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <label style={{ fontSize: '0.88rem', fontWeight: 800, color: '#1e293b', margin: 0, display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span>🎥</span> Upload Event Video / Media File
                  </label>
                  {uploadingVideo && (
                    <span style={{ fontSize: '0.8rem', color: '#7c3aed', fontWeight: 700 }}>
                      ⏳ Uploading Video...
                    </span>
                  )}
                </div>

                {videoUploadError && (
                  <div style={{ padding: '8px 12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', color: '#dc2626', fontSize: '0.82rem', marginBottom: '10px' }}>
                    ⚠️ {videoUploadError}
                  </div>
                )}

                {/* Uploaded Video Preview */}
                {eventFormData.videoUrl ? (
                  <div style={{ background: '#ffffff', padding: '12px 14px', borderRadius: '10px', border: '1.5px solid #a7f3d0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', overflow: 'hidden' }}>
                      <span style={{ fontSize: '24px' }}>🎬</span>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: '0.88rem', color: '#065f46', wordBreak: 'break-all' }}>
                          {eventFormData.videoFileName || 'Attached Video File'}
                        </div>
                        <div style={{ fontSize: '0.78rem', color: '#64748b' }}>
                          {eventFormData.videoFileSize ? `${(eventFormData.videoFileSize / (1024 * 1024)).toFixed(2)} MB • ` : ''}
                          <a href={eventFormData.videoUrl} target="_blank" rel="noreferrer" style={{ color: '#0284c7', textDecoration: 'underline', fontWeight: 600 }}>
                            Preview Video File ↗
                          </a>
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={handleRemoveVideo}
                      style={{
                        padding: '6px 12px',
                        background: '#fee2e2',
                        color: '#b91c1c',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '0.8rem',
                        fontWeight: 700,
                        cursor: 'pointer',
                      }}
                    >
                      ✕ Remove
                    </button>
                  </div>
                ) : (
                  <div>
                    <label
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '2px dashed #cbd5e1',
                        borderRadius: '10px',
                        padding: '16px 20px',
                        background: '#ffffff',
                        cursor: uploadingVideo ? 'not-allowed' : 'pointer',
                        textAlign: 'center',
                        transition: 'all 0.15s ease',
                      }}
                    >
                      <input
                        type="file"
                        accept="video/*,.mp4,.mov,.webm,.mkv,.avi,.pdf"
                        onChange={handleVideoFileUpload}
                        disabled={uploadingVideo}
                        style={{ display: 'none' }}
                      />
                      <span style={{ fontSize: '28px', marginBottom: '6px' }}>📁</span>
                      <span style={{ fontWeight: 700, color: '#475569', fontSize: '0.88rem' }}>
                        {uploadingVideo ? 'Uploading Video File...' : 'Click or Drag & Drop to Upload Video / File'}
                      </span>
                      <span style={{ fontSize: '0.78rem', color: '#94a3b8', marginTop: '4px' }}>
                        Supports MP4, WebM, MOV, MKV, AVI, etc. (up to 200MB)
                      </span>
                    </label>
                  </div>
                )}
              </div>

              {/* Description */}
              <div style={{ marginBottom: '18px' }}>
                <label style={{ display: 'block', fontSize: '0.88rem', fontWeight: 700, color: '#1e293b', marginBottom: '6px' }}>
                  Event Description & Agenda
                </label>
                <textarea
                  rows="3"
                  placeholder="Outline key session takeaways, clinical concepts, and target audience..."
                  value={eventFormData.description}
                  onChange={(e) => setEventFormData({ ...eventFormData, description: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '11px 14px',
                    borderRadius: '10px',
                    border: '1.5px solid #cbd5e1',
                    fontSize: '0.9rem',
                    outline: 'none',
                    resize: 'vertical',
                    boxSizing: 'border-box',
                  }}
                ></textarea>
              </div>

              {/* Publication Status & Direct Approve Setting */}
              <div style={{ marginBottom: '22px', background: '#f8fafc', padding: '16px', borderRadius: '12px', border: '1.5px solid #e2e8f0' }}>
                <label style={{ display: 'block', fontSize: '0.88rem', fontWeight: 800, color: '#1e293b', marginBottom: '8px' }}>
                  ⚡ Publication Status (Direct Approve)
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
                  {/* Approved */}
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '10px 12px',
                      borderRadius: '10px',
                      border: eventFormData.status === 'approved' ? '2px solid #10b981' : '1.5px solid #cbd5e1',
                      background: eventFormData.status === 'approved' ? '#ecfdf5' : '#ffffff',
                      cursor: 'pointer',
                      fontSize: '0.85rem',
                      fontWeight: 700,
                      color: eventFormData.status === 'approved' ? '#065f46' : '#475569',
                    }}
                  >
                    <input
                      type="radio"
                      name="eventStatusOption"
                      checked={eventFormData.status === 'approved'}
                      onChange={() => setEventFormData({ ...eventFormData, status: 'approved' })}
                    />
                    <span>✓ Direct Approve</span>
                  </label>

                  {/* Pending */}
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '10px 12px',
                      borderRadius: '10px',
                      border: eventFormData.status === 'pending' ? '2px solid #f59e0b' : '1.5px solid #cbd5e1',
                      background: eventFormData.status === 'pending' ? '#fffbeb' : '#ffffff',
                      cursor: 'pointer',
                      fontSize: '0.85rem',
                      fontWeight: 700,
                      color: eventFormData.status === 'pending' ? '#92400e' : '#475569',
                    }}
                  >
                    <input
                      type="radio"
                      name="eventStatusOption"
                      checked={eventFormData.status === 'pending'}
                      onChange={() => setEventFormData({ ...eventFormData, status: 'pending' })}
                    />
                    <span>⏳ Pending</span>
                  </label>

                  {/* Rejected */}
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '10px 12px',
                      borderRadius: '10px',
                      border: eventFormData.status === 'rejected' ? '2px solid #ef4444' : '1.5px solid #cbd5e1',
                      background: eventFormData.status === 'rejected' ? '#fef2f2' : '#ffffff',
                      cursor: 'pointer',
                      fontSize: '0.85rem',
                      fontWeight: 700,
                      color: eventFormData.status === 'rejected' ? '#991b1b' : '#475569',
                    }}
                  >
                    <input
                      type="radio"
                      name="eventStatusOption"
                      checked={eventFormData.status === 'rejected'}
                      onChange={() => setEventFormData({ ...eventFormData, status: 'rejected' })}
                    />
                    <span>✕ Rejected</span>
                  </label>
                </div>
              </div>

              {/* Modal Actions */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', borderTop: '1px solid #f1f5f9', paddingTop: '18px' }}>
                <button
                  type="button"
                  onClick={() => setEventModal({ isOpen: false, mode: 'create', eventId: null })}
                  disabled={isSubmitting}
                  style={{
                    padding: '10px 20px',
                    borderRadius: '10px',
                    border: '1.5px solid #cbd5e1',
                    background: '#ffffff',
                    color: '#475569',
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  style={{
                    padding: '10px 24px',
                    borderRadius: '10px',
                    border: 'none',
                    background: 'linear-gradient(135deg, #7c3aed 0%, #6366f1 100%)',
                    color: '#ffffff',
                    fontWeight: 700,
                    cursor: 'pointer',
                    boxShadow: '0 4px 12px rgba(124, 58, 237, 0.25)',
                  }}
                >
                  {isSubmitting
                    ? 'Saving...'
                    : eventModal.mode === 'create'
                    ? '🚀 Create & Direct Approve'
                    : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* EVENT DETAIL VIEW MODAL */}
      {/* ========================================================================= */}
      {selectedEvent && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(15, 23, 42, 0.65)',
            backdropFilter: 'blur(5px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px',
          }}
          onClick={() => setSelectedEvent(null)}
        >
          <div
            style={{
              background: '#ffffff',
              borderRadius: '20px',
              padding: '28px',
              maxWidth: '600px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.3)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', borderBottom: '1px solid #f1f5f9', paddingBottom: '14px' }}>
              <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 800, color: '#1e293b' }}>
                🎉 Event Information
              </h3>
              <button
                onClick={() => setSelectedEvent(null)}
                style={{ border: 'none', background: '#f1f5f9', borderRadius: '50%', width: '32px', height: '32px', fontSize: '1rem', cursor: 'pointer', color: '#64748b' }}
              >
                ✕
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', fontSize: '0.92rem', color: '#334155' }}>
              <div>
                <label style={{ fontWeight: 700, color: '#64748b', display: 'block', fontSize: '0.8rem', textTransform: 'uppercase' }}>Event Title</label>
                <div style={{ fontSize: '1.1rem', fontWeight: 800, color: '#1e293b', marginTop: '2px' }}>{selectedEvent.title}</div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{ fontWeight: 700, color: '#64748b', display: 'block', fontSize: '0.8rem', textTransform: 'uppercase' }}>Host / Creator</label>
                  <div style={{ fontWeight: 600 }}>👤 {selectedEvent.createdBy?.name || 'Administrator'}</div>
                  <div style={{ fontSize: '0.78rem', color: '#94a3b8' }}>{selectedEvent.createdBy?.email || 'N/A'}</div>
                </div>

                <div>
                  <label style={{ fontWeight: 700, color: '#64748b', display: 'block', fontSize: '0.8rem', textTransform: 'uppercase' }}>Type & Mode</label>
                  <div style={{ textTransform: 'capitalize', fontWeight: 600 }}>
                    {selectedEvent.eventType || 'seminar'} • {selectedEvent.mode === 'online' ? '💻 Online' : '🏥 Offline'}
                  </div>
                </div>
              </div>

              <div>
                <label style={{ fontWeight: 700, color: '#64748b', display: 'block', fontSize: '0.8rem', textTransform: 'uppercase' }}>Scheduled Date & Time</label>
                <div style={{ fontWeight: 600 }}>📅 {selectedEvent.scheduledAt ? new Date(selectedEvent.scheduledAt).toLocaleString() : 'TBD'}</div>
              </div>

              {selectedEvent.videoUrl && (
                <div>
                  <label style={{ fontWeight: 700, color: '#64748b', display: 'block', fontSize: '0.8rem', textTransform: 'uppercase', marginBottom: '6px' }}>
                    🎬 Event Video / File Attachment
                  </label>
                  <div style={{ background: '#f8fafc', padding: '12px 14px', borderRadius: '10px', border: '1.5px solid #e2e8f0' }}>
                    <div style={{ fontWeight: 700, fontSize: '0.88rem', color: '#1e293b', marginBottom: '8px' }}>
                      📁 {selectedEvent.videoFileName || 'Attached Event Video'}
                      {selectedEvent.videoFileSize ? ` (${(selectedEvent.videoFileSize / (1024 * 1024)).toFixed(2)} MB)` : ''}
                    </div>
                    {selectedEvent.videoUrl.match(/\.(mp4|webm|ogg|mov)$/i) ? (
                      <video
                        controls
                        src={selectedEvent.videoUrl}
                        style={{ width: '100%', maxHeight: '240px', borderRadius: '8px', background: '#000' }}
                      />
                    ) : (
                      <a
                        href={selectedEvent.videoUrl}
                        target="_blank"
                        rel="noreferrer"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', color: '#2563eb', fontWeight: 700, textDecoration: 'underline' }}
                      >
                        ▶️ Open / Download Attached Video File ↗
                      </a>
                    )}
                  </div>
                </div>
              )}

              {selectedEvent.mode === 'online' && selectedEvent.meetingLink && (
                <div>
                  <label style={{ fontWeight: 700, color: '#64748b', display: 'block', fontSize: '0.8rem', textTransform: 'uppercase' }}>Meeting Link</label>
                  <a href={selectedEvent.meetingLink} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', fontWeight: 600, wordBreak: 'break-all' }}>
                    {selectedEvent.meetingLink}
                  </a>
                </div>
              )}

              {selectedEvent.mode === 'offline' && selectedEvent.location && (
                <div>
                  <label style={{ fontWeight: 700, color: '#64748b', display: 'block', fontSize: '0.8rem', textTransform: 'uppercase' }}>Venue Address</label>
                  <div style={{ fontWeight: 600 }}>🏥 {selectedEvent.location}</div>
                </div>
              )}

              {selectedEvent.description && (
                <div>
                  <label style={{ fontWeight: 700, color: '#64748b', display: 'block', fontSize: '0.8rem', textTransform: 'uppercase', marginBottom: '4px' }}>Description & Agenda</label>
                  <div style={{ background: '#f8fafc', padding: '12px', borderRadius: '10px', border: '1px solid #e2e8f0', fontSize: '0.88rem', lineHeight: 1.5 }}>
                    {selectedEvent.description}
                  </div>
                </div>
              )}

              <div>
                <label style={{ fontWeight: 700, color: '#64748b', display: 'block', fontSize: '0.8rem', textTransform: 'uppercase', marginBottom: '4px' }}>Current Status</label>
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '4px 12px',
                    background: selectedEvent.status === 'approved' ? '#ecfdf5' : selectedEvent.status === 'rejected' ? '#fef2f2' : '#fffbeb',
                    color: selectedEvent.status === 'approved' ? '#065f46' : selectedEvent.status === 'rejected' ? '#991b1b' : '#92400e',
                    border: `1px solid ${selectedEvent.status === 'approved' ? '#a7f3d0' : selectedEvent.status === 'rejected' ? '#fecaca' : '#fde68a'}`,
                    borderRadius: '20px',
                    fontSize: '0.82rem',
                    fontWeight: '700',
                  }}
                >
                  {selectedEvent.status === 'approved' ? '✓ Approved' : selectedEvent.status === 'rejected' ? '✕ Rejected' : '⏳ Pending Review'}
                </span>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '24px', borderTop: '1px solid #f1f5f9', paddingTop: '16px' }}>
              <button
                type="button"
                onClick={() => setSelectedEvent(null)}
                style={{
                  padding: '9px 18px',
                  background: '#ffffff',
                  color: '#475569',
                  border: '1.5px solid #cbd5e1',
                  borderRadius: '8px',
                  fontWeight: '600',
                  cursor: 'pointer',
                }}
              >
                Close
              </button>

              <button
                type="button"
                onClick={() => {
                  const ev = selectedEvent;
                  setSelectedEvent(null);
                  handleOpenEditModal(ev);
                }}
                style={{
                  padding: '9px 18px',
                  background: '#f5f3ff',
                  color: '#7c3aed',
                  border: '1.5px solid #ddd6fe',
                  borderRadius: '8px',
                  fontWeight: '700',
                  cursor: 'pointer',
                }}
              >
                ✏️ Edit Event
              </button>

              {selectedEvent.status !== 'approved' && (
                <button
                  type="button"
                  onClick={() => handleReviewDecision(selectedEvent._id, 'approved')}
                  disabled={isSubmitting}
                  style={{
                    padding: '9px 18px',
                    background: '#10b981',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: '700',
                    cursor: 'pointer',
                  }}
                >
                  ✓ Approve Event
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* REJECTION REASON MODAL */}
      {/* ========================================================================= */}
      {reviewNoteModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(15, 23, 42, 0.65)',
            backdropFilter: 'blur(5px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px',
          }}
        >
          <div
            style={{
              background: '#ffffff',
              borderRadius: '20px',
              padding: '28px',
              maxWidth: '480px',
              width: '100%',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.3)',
            }}
          >
            <h4 style={{ margin: '0 0 8px 0', fontSize: '1.2rem', fontWeight: 800, color: '#1e293b' }}>
              Reject Event Request
            </h4>
            <p style={{ margin: '0 0 16px 0', fontSize: '0.88rem', color: '#64748b' }}>
              Provide feedback explaining why this event was rejected.
            </p>

            <textarea
              value={reviewNoteInput}
              onChange={(e) => setReviewNoteInput(e.target.value)}
              placeholder="Reason for rejection or requested changes..."
              rows="4"
              style={{
                width: '100%',
                padding: '10px 14px',
                border: '1.5px solid #cbd5e1',
                borderRadius: '10px',
                fontSize: '0.9rem',
                boxSizing: 'border-box',
                marginBottom: '20px',
                outline: 'none',
              }}
            ></textarea>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setReviewNoteModal(null)}
                style={{
                  padding: '9px 18px',
                  background: '#ffffff',
                  color: '#475569',
                  border: '1.5px solid #cbd5e1',
                  borderRadius: '8px',
                  fontWeight: '600',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleReviewDecision(reviewNoteModal.eventId, 'rejected', reviewNoteInput)}
                disabled={isSubmitting}
                style={{
                  padding: '9px 20px',
                  background: '#ef4444',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: '700',
                  cursor: 'pointer',
                }}
              >
                Confirm Rejection
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminEventManagement;
